"use client";

import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import TimingReportCTA from "@/components/TimingReportCTA";
import { PanchangCityProvider, usePanchangCity } from "@/contexts/PanchangCity";
import { getPanchang, type PanchangResponse } from "@/lib/api/panchang";
import { getBrowserQueryClient } from "@/lib/query-client";
import type { DailyTimingTelemetry } from "./daily-timing-experiment";
import {
  ControlMainTithiCard,
  ControlPanchangSnapshot,
  ControlSignificanceCard,
  ControlTithiHeader,
  ControlTithiTypesReference,
} from "./tithi-today.control-sections";

export interface TithiTodayControlProps {
  showHeader?: boolean;
  language?: "en" | "hi";
  sourcePath?: "/tithi-today" | "/hi/tithi-today";
  initialDate: string;
  initialResult?: PanchangResponse | null;
  crossLinks: ReactNode;
  telemetry?: DailyTimingTelemetry;
}

export default function TithiTodayControl(props: TithiTodayControlProps) {
  return (
    <PanchangCityProvider>
      <TithiTodayControlContent {...props} />
    </PanchangCityProvider>
  );
}

function TithiTodayControlContent({
  showHeader = true,
  language = "en",
  sourcePath = "/tithi-today",
  initialDate,
  initialResult = null,
  crossLinks,
  telemetry,
}: TithiTodayControlProps) {
  const { city } = usePanchangCity();
  const date = useMemo(() => initialDate, [initialDate]);
  const initialData =
    city.slug === "new-delhi" && initialResult?.date === date ? initialResult : undefined;
  const {
    data: result,
    isLoading,
    isError,
  } = useQuery(
    {
      queryKey: ["panchang", city.slug, date],
      queryFn: () =>
        getPanchang({
          date,
          latitude: city.latitude,
          longitude: city.longitude,
          tz: city.timezone,
        }),
      initialData,
      staleTime: 30 * 60 * 1000,
      retry: 1,
    },
    getBrowserQueryClient()
  );
  const tithi = result?.tithi;
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (result) telemetry?.answerRendered(result, city.slug);
  }, [city.slug, result, telemetry]);

  useEffect(() => {
    if (!result || isLoading || isError) return;
    const count = surfaceRef.current?.querySelectorAll('[data-primary-cta="true"]').length ?? 0;
    telemetry?.primaryCtaAudited(count);
  }, [isError, isLoading, result, telemetry]);

  return (
    <div
      ref={surfaceRef}
      className="mx-auto max-w-2xl px-4 pb-10 pt-4 sm:px-6"
      data-daily-timing-surface="control"
    >
      {showHeader && (
        <ControlTithiHeader language={language} date={date} tithi={tithi} cityName={city.name} />
      )}
      <ControlMainTithiCard
        language={language}
        isLoading={isLoading}
        isError={isError}
        tithi={tithi}
      />
      <ControlSignificanceCard language={language} isLoading={isLoading} tithi={tithi} />
      <ControlPanchangSnapshot
        language={language}
        isLoading={isLoading}
        result={result}
        cityName={city.name}
      />
      {result && !isLoading && !isError && (
        <div data-primary-cta="true">
          <TimingReportCTA language={language} sourcePath={sourcePath} />
        </div>
      )}
      <ControlTithiTypesReference language={language} />
      {crossLinks}
    </div>
  );
}
